/**
 * Klient mirror serverové `src/server/utils/chartBucketing.ts`. Konstanty MUSÍ
 * zůstat v sync — jiné resolution thresholds by způsobily tile-cache miss
 * (klient by žádal tile bounds, které server nezarovnává stejně).
 *
 * Viz docs/plans/CHART-DATA-REFACTOR.md (Fáze 2).
 */

export type ChartResolutionKey =
  | "raw"
  | "bucket-10s"
  | "bucket-30s"
  | "bucket-1m"
  | "bucket-2m"
  | "bucket-5m"
  | "bucket-15m"
  | "bucket-30m"
  | "stat-hour"
  | "stat-day";

export const bucketSecondsByResolution: Record<ChartResolutionKey, number> = {
  raw: 0,
  "bucket-10s": 10,
  "bucket-30s": 30,
  "bucket-1m": 60,
  "bucket-2m": 120,
  "bucket-5m": 300,
  "bucket-15m": 900,
  "bucket-30m": 1800,
  "stat-hour": 3600,
  "stat-day": 86400,
};

export const tileSizeMsByResolution: Record<ChartResolutionKey, number> = {
  raw: 60 * 60 * 1000,
  "bucket-10s": 30 * 60 * 1000,
  "bucket-30s": 60 * 60 * 1000,
  "bucket-1m": 2 * 60 * 60 * 1000,
  "bucket-2m": 4 * 60 * 60 * 1000,
  "bucket-5m": 12 * 60 * 60 * 1000,
  "bucket-15m": 24 * 60 * 60 * 1000,
  "bucket-30m": 2 * 24 * 60 * 60 * 1000,
  "stat-hour": 7 * 24 * 60 * 60 * 1000,
  "stat-day": 30 * 24 * 60 * 60 * 1000,
};

export const resolveResolution = (rangeMs: number, pixelWidth: number): ChartResolutionKey => {
  if (rangeMs <= 0 || pixelWidth <= 0) return "raw";
  const idealBucketMs = (rangeMs / pixelWidth) * 3;

  if (idealBucketMs < 10_000) return "raw";
  if (idealBucketMs < 30_000) return "bucket-10s";
  if (idealBucketMs < 60_000) return "bucket-30s";
  if (idealBucketMs < 120_000) return "bucket-1m";
  if (idealBucketMs < 300_000) return "bucket-2m";
  if (idealBucketMs < 900_000) return "bucket-5m";
  if (idealBucketMs < 1_800_000) return "bucket-15m";
  if (idealBucketMs < 3_600_000) return "bucket-30m";
  if (idealBucketMs < 86_400_000) return "stat-hour";
  return "stat-day";
};

export const alignToTile = (ts: number, resolution: ChartResolutionKey): number => {
  const tileSize = tileSizeMsByResolution[resolution];
  return Math.floor(ts / tileSize) * tileSize;
};

export const tileRangesForRange = (
  resolution: ChartResolutionKey,
  start: number,
  end: number
): { start: number; end: number }[] => {
  const tileSize = tileSizeMsByResolution[resolution];
  const ranges: { start: number; end: number }[] = [];
  const firstTile = alignToTile(start, resolution);
  for (let t = firstTile; t < end; t += tileSize) {
    ranges.push({ start: t, end: t + tileSize });
  }
  return ranges;
};

/**
 * Tile cache key — deterministický string pro daný (topic, resolution, tile range).
 * Stejný key napříč requesty = cache hit.
 */
export const makeTileKey = (topicId: string, resolution: ChartResolutionKey, tileStart: number): string =>
  `${topicId}|${resolution}|${tileStart}`;

/**
 * Approximate byte cost per bod (pro LRU memory budgeting). 64 B = JS object overhead
 * + 3-4 numbers (ts, v, min?, max?). Pesimistic upper bound.
 */
export const APPROX_BYTES_PER_POINT = 64;

/**
 * Soft cap na celkovou paměť tile cache (LRU eviction past this point). 100 MB.
 */
export const CHART_CACHE_MAX_BYTES = 100 * 1024 * 1024;

/**
 * Per-tile TTL: tile pro aktuální okno (`tileEnd > now - 60s`) má TTL 30s
 * (data se ještě dotékají), tile čistě historický → bez TTL (data immutable).
 */
export const isTileFresh = (tileEnd: number, fetchedAt: number, now: number): boolean => {
  const isCurrent = tileEnd > now - 60_000;
  if (!isCurrent) return true; // historický tile — nikdy neexpiruje
  return now - fetchedAt < 30_000;
};
